import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const WIN_REASONS = ['Competitive Win', 'Better Pricing', 'Better Product Fit', 'Relationship', 'Other'];
const LOST_REASONS = ['Budget', 'Timing', 'Competitor', 'No Response', 'Other'];

/**
 * Shared "Mark Won" / "Mark Lost" modal for DealsPage and DealDetailPage —
 * collects a structured reason category plus an optional free-text note,
 * instead of a bare confirm() prompt, so win/loss analysis has real categories
 * to roll up instead of unstructured free text alone.
 */
export default function DealOutcomeModal({ open, mode, dealTitle, onClose, onConfirm }) {
  const isWon = mode === 'won';
  const options = isWon ? WIN_REASONS : LOST_REASONS;
  const [reasonCategory, setReasonCategory] = useState(options[0]);
  const [note, setNote] = useState('');
  const [competitorName, setCompetitorName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const showCompetitorField = !isWon && reasonCategory === 'Competitor';

  useEffect(() => {
    if (open) {
      setReasonCategory(options[0]);
      setNote('');
      setCompetitorName('');
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  if (!open) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm({ reasonCategory, note, competitorName: showCompetitorField ? competitorName : undefined });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className={`px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between ${isWon ? 'bg-[#EBF3ED]' : 'bg-[#FDE7E9]'}`}>
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">{isWon ? 'Mark Deal as Won' : 'Mark Deal as Lost'}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          <p className="text-[13px] text-[#706E6B]">
            {isWon ? 'Sealing' : 'Closing'} <span className="font-bold text-[#080707]">{dealTitle}</span> as {isWon ? 'WON' : 'LOST'}. Select the reason.
          </p>

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">{isWon ? 'Win Reason' : 'Lost Reason'}</label>
            <select
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
              className="w-full h-9 px-3 bg-white border border-[#C9C9C9] rounded text-[13px] font-bold outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
            >
              {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>

          {showCompetitorField && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-[#706E6B]">Competitor Name</label>
              <input
                type="text"
                value={competitorName}
                onChange={(e) => setCompetitorName(e.target.value)}
                placeholder="Who won this deal?"
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">Note (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Add any extra context…"
              className="w-full px-3 py-2 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all resize-none focus:ring-1 focus:ring-[#0176D3]"
            />
          </div>

          <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
            <button type="button" onClick={onClose} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className={`flex-1 h-10 rounded text-[13px] font-bold text-white shadow-[0_2px_8px_rgba(0,0,0,0.16)] disabled:opacity-50 ${isWon ? 'bg-[#2E844A] hover:bg-[#1c6b37]' : 'bg-[#BA0517] hover:bg-[#8f0411]'}`}
            >
              {submitting ? 'Saving…' : isWon ? 'Confirm Won' : 'Confirm Lost'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
